import { IS_BROWSER, APPConfig } from 'config/appConfig'
import { asyncLocalStorageObject } from 'src/utils/asyncLocalStorage'
import ContentStack from 'src/api/contentStack'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { nextGenTranslationContent } from 'src/deps/NextGenTranslationContent'
import { convertToBoolean } from 'src/utils/commonUtils'
const TRANSLATION_CACHE_URL_SUFFIX = '_translationCache'
const TRANSLATION_CACHE_MINS = 5
const TRANSLATION_CACHE_TTL = 1000 * 60
let isPlpPdpRedesign = false

class TranslationStore {
  constructor() {
    this.cache = {}
    this.isClientTranslationLoadedAlready = false
  }
  translate = (
    localeKey = '',
    defaultValue = '',
    nameSpace = 'csr-resource',
    isNextGenTranslations = false
  ) => {
    try {
      if (isNextGenTranslations) {
        if (
          Object.keys(nextGenTranslationContent.nextGenTranslationContentValue)
            .length > 0 &&
          localeKey
        ) {
          const translateValue =
            nextGenTranslationContent.nextGenTranslationContentValue[
              localeKey
            ] ||
            defaultValue ||
            ''
          return translateValue
        }
        return defaultValue || ''
      }
      let resources = {}
      const transformedKey = localeKey
        .replaceAll('.', '_')
        .replaceAll(' ', '_')
        .replaceAll('-', '_')
        .replaceAll(',', '_')
        .toLowerCase()
      if (IS_BROWSER) {
        resources = window.translationResources || {}
      } else {
        try {
          resources =
            asyncLocalStorageObject
              .getStore?.()
              ?.get?.('translationResources') || {}
          // console.log(' asyncLocalStorageObject resources', resources)
        } catch (e) {
          console.error('error in translate getStore --> server', e)
          resources = {}
        }
      }
      const translateValue = resources[transformedKey] || defaultValue || ''
      return translateValue
    } catch (e) {
      console.error('error in translate ', e)
      return defaultValue
    }
  }

  setPageToCache = async (cacheFilePath, resources) => {
    const cacheTimeMins =
      APPConfig.getAppConfig()?.serverProperties?.cacheTime ??
      TRANSLATION_CACHE_MINS
    const timer = TRANSLATION_CACHE_TTL * cacheTimeMins
    try {
      const date = new Date()
      const cacheArr = []
      cacheArr.push({
        key: cacheFilePath,
        value: { cacheFilePath: cacheFilePath, resources: resources },
      })
      cacheArr.push({
        key: cacheFilePath + '_time',
        value: date.getTime() + timer,
      })
      await this.cache.save?.(cacheArr)
      // console.log('save to cache')
    } catch (e) {
      console.error('Fn setPageToCache translation', e)
    }
  }

  getPageFromCache = async (cacheFilePath = '') => {
    try {
      const expiryTime = await this.cache.get?.(cacheFilePath + '_time')
      // console.log('expiryTime', expiryTime || {})
      const date = new Date()
      const ttl = date.getTime()

      const cachedTime = expiryTime || 0
      let isTTLExpired = false
      if (ttl > cachedTime) {
        await this.cache.remove?.(cacheFilePath)
        isTTLExpired = true
      }
      if (isTTLExpired) {
        // console.log('expired', cacheFilePath)
        return ''
      } else {
        // console.log('not expired', cacheFilePath)
        const fileFromCache = await this.cache.get?.(cacheFilePath)
        return fileFromCache
      }
    } catch (e) {
      console.error('Fn getPageFromCache translation ', e)
    }
  }

  fetchFromCacheOrAPI = async (req = {}, namespace = 'ssr-resource') => {
    /* check for Cache or make API */
    try {
      const localeCode = this.getLocaleCode(req)
      const environment = APPConfig.getAppConfig().CS_ENV || 'dev'

      const cacheFilePath =
        localeCode + '_' + namespace + '_' + TRANSLATION_CACHE_URL_SUFFIX
      let resources = {}
      const debuggerKey = '_dataForDebugging_' + namespace.replace('-', '_')
      const dataForDebugging = {
        [debuggerKey]: {
          originalUrl: req.originalUrl || '',
          localeCode: localeCode,
          environment,
          namespace: namespace,
          isFromCache: '',
        },
      }
      if (!IS_BROWSER) {
        let DataFromCache = await this.getPageFromCache(cacheFilePath)

        if (DataFromCache) {
          resources = DataFromCache.resources || {}
          dataForDebugging[debuggerKey].isFromCache = 'CachedFile'
          return { ...resources, ...dataForDebugging }
        }
      }
      isPlpPdpRedesign =
        convertToBoolean(APPConfig.getAppConfig()?.enablePdpRedesign) ||
        convertToBoolean(APPConfig.getAppConfig()?.enablePlpRedesign) ||
        false
      // console.log('not available in cache')
      dataForDebugging[debuggerKey].isFromCache = 'NotCacheFile_FromAPI'
      if (namespace == 'ssr-resource') {
        resources = await this.fetchSSRTranslationsFromAPI(
          localeCode,
          req.originalUrl || ''
        )
      } else {
        resources = await this.fetchCSRTranslationsFromAPI(localeCode)
      }
      const responseWithDebugging = {
        ...(resources || {}),
        ...dataForDebugging,
      }
      if (!IS_BROWSER) {
        this.setPageToCache(cacheFilePath, { ...responseWithDebugging })
      }
      return responseWithDebugging
    } catch (e) {
      console.error('error in fetchTranslations ', e)
      return {
        error: 'error in fetchFromCacheOrAPI -->' + JSON.stringify(e),
      }
    }
  }

  getLocaleCode = (req = {}) => {
    let pathParams = ''
    const localeFromUrl = getLocaleCodeFromUrl({
      url: IS_BROWSER
        ? window.location?.pathname || ''
        : req?.originalUrl || '',
      at: 'pathParam',
      defaultLocale: 'en_US',
      isReverseType: true,
    })
    const splittedArray = localeFromUrl?.split?.('_') || []
    const country = (splittedArray[1] || 'US').toUpperCase()
    const language = (splittedArray[0] || 'en').toLowerCase()
    return `${country}_${language}`
  }

  todo_fetchCSRTranslationsFromAPI = async (req = {}) => {
    // https://cdn.contentstack.io/v3/content_types/
    // equinox_all_storefront_csr_translations/entries/blt3927bf010b9955a4?
    // environment=public-test&locale=US-en&include[]=csr_all&include_fallback=true",

    const locale = this.getLocaleCode(req)
    const csLoadParams = {
      endPointName: 'csrTranslation',
      curLocaleFromUrl: locale,
    }
    const loadParams = {
      credentials: 'omit',
      isToIncludeCommomHeaders: false,
      locale,
    }
    response = await commonContainer.fetchResponse(csLoadParams)
    if (commonContainer.isSuccessResponse(response)) {
      return response
    }
    return {}
  }

  groupAllEntries = response => {
    let groupedResources = {}
    if (response && response.pdp_strings_reference) {
      response.pdp_strings_reference.forEach?.(entry => {
        groupedResources = {
          ...groupedResources,
          ...entry,
        }
      })
    }
    if (response && response?.product_offer_strings_batch_2) {
      const shareOffer = response?.product_offer_strings_batch_2?.[0] || {}
      if (shareOffer?.share_your_offer_group) {
        groupedResources = {
          ...groupedResources,
          ...shareOffer.share_your_offer_group,
          ...shareOffer.share_your_offer_group.performance_data,
        }
      }
      if (shareOffer?.social_media_links?.links) {
        if (IS_BROWSER) {
          window.social_share = shareOffer?.social_media_links?.links
        } else {
          groupedResources = {
            ...groupedResources,
            social_share: shareOffer?.social_media_links?.links,
          }
        }
      }
    }
    if (response && response.all_entries) {
      response.all_entries.forEach?.(entry => {
        groupedResources = {
          ...groupedResources,
          ...entry,
        }
      })
    }
    if (response && response.account_down_convert_ba_to_member_tool_reference) {
      response.account_down_convert_ba_to_member_tool_reference.forEach?.(
        entry => {
          groupedResources = {
            ...groupedResources,
            ...entry,
          }
        }
      )
    }

    return groupedResources
  }

  fetchSSRTranslationsFromAPI = async (localeCode = '', reqURL = '') => {
    let locale = localeCode.replace('_', '-')
    const response = await ContentStack.getSSRTranslationEntry(locale)
    const ssrResources = this.groupAllEntries(response || {})
    let allResources = {}
    const pageResources =
      (await this.handlePageTranslation(locale, reqURL)) || {}
    allResources = {
      ...ssrResources,
      ...pageResources,
    }
    ssrResources.reqURL = reqURL

    return isPlpPdpRedesign ? allResources : ssrResources
  }

  fetchCSRTranslationsFromAPI = async (localeCode = '') => {
    let locale = localeCode.replace('_', '-')
    const response = await ContentStack.getCSRTranslationEntry(locale)
    const csrResources = this.groupAllEntries(response || {})
    let allResources = {}
    const pageResources = (await this.handlePageTranslation(locale)) || {}
    allResources = {
      ...csrResources,
      ...pageResources,
    }
    return isPlpPdpRedesign ? allResources : csrResources
  }

  checkAndTriggerPageTranslation = async (fromRoutes = false) => {
    if (fromRoutes && IS_BROWSER) {
      if (Object.keys(window?.translationResources)?.length > 0) {
        const localeValue = this.getLocaleCode()?.replace('_', '-')
        const pageResourcesData =
          (await this.handlePageTranslation(localeValue)) || {}
        window.translationResources = {
          ...(window.translationResources || {}),
          ...(pageResourcesData || {}),
        }
      }
      return {}
    }
    return {}
  }

  handlePageTranslation = async (locale = 'en-us', reqURL = '') => {
    if (isPlpPdpRedesign) {
      const pageTranslationresponse =
        await ContentStack.getPageTranslationEntry(locale, reqURL)
      const pageResourcesData = this.groupPageEntries(
        pageTranslationresponse || {}
      )
      return pageResourcesData
    }
    return {}
  }

  groupPageEntries = response => {
    let groupedPageResources = {}
    if (response && response?.common_strings) {
      response?.common_strings?.forEach?.(entry => {
        const entryPrefix = entry?.key?.toLowerCase() || ''
        entry?.strings?.forEach?.(entries => {
          groupedPageResources[
            entryPrefix +
              '_' +
              (entries?.key || '')?.toLowerCase()?.replaceAll(' ', '_')
          ] = entries?.value || ''
        })
      })
    }
    if (response && response?.common_strings_overrides?.strings) {
      const entryPrefix = 'override_strings_'
      response?.common_strings_overrides?.strings?.forEach?.(entries => {
        groupedPageResources[
          entryPrefix +
            (entries?.key || '')?.toLowerCase()?.replaceAll(' ', '_')
        ] = entries?.text || ''
      })
    }

    const jsonKeyParse = (obj, prefix = '') => {
      const result = {}
      for (const key in obj) {
        const value = obj[key]
        let newKey = prefix ? `${prefix}_${key}` : key

        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value) &&
          newKey !== 'override_text_strings'
        ) {
          if (key === 'common_strings_overrides') newKey = 'override_text'
          Object.assign(result, jsonKeyParse(value, newKey))
        } else if (
          key !== 'common_strings' &&
          key !== 'common_strings_overrides' &&
          newKey !== 'override_text_strings'
        ) {
          result[newKey] = value
        }
      }

      return result
    }
    const flattenedResources = jsonKeyParse(response)
    groupedPageResources = {
      ...groupedPageResources,
      ...flattenedResources,
    }
    return groupedPageResources
  }

  fetchTranslationForBrowser = async (isFromSSR = false) => {
    if (IS_BROWSER) {
      window.enableTranslationFromContentStack = true
      this.isClientTranslationLoadedAlready = true
      const csrResourcesData = await this.fetchFromCacheOrAPI(
        {},
        'csr-resource'
      )
      let combinedResources = {}
      if (isFromSSR) {
        // for ssr build,
        // ssr translations will be loaded in server
        // and will be available in window.translationResources
        combinedResources = {
          ...(window.translationResources || {}),
          ...(csrResourcesData || {}),
        }
      } else {
        // for local builds -(non production)
        const ssrResourcesData = await this.fetchFromCacheOrAPI(
          {},
          'ssr-resource'
        )
        combinedResources = {
          ...(ssrResourcesData || {}),
          ...(csrResourcesData || {}),
        }
      }
      window.translationResources = {
        ...combinedResources,
      }
    }
  }

  fetchTranslationForServer = async (req = {}, isServerPage = false, cache) => {
    let csrResourcesData = {}
    let isCSRResourcesLoadedInServer = false
    this.cache = cache
    const localeCode = this.getLocaleCode(req)
    const ssrResourcesData = await this.fetchFromCacheOrAPI(req, 'ssr-resource')
    // if (!isServerPage) {
    //   csrResourcesData = await this.fetchFromCacheOrAPI(req, 'csr-resource')
    //   isCSRResourcesLoadedInServer = true
    // }

    asyncLocalStorageObject.getStore().set('translationResources', {
      // ...combinedResources,
      ...(ssrResourcesData || {}),
    })
  }
}

const translationStore = new TranslationStore()

export { translationStore }
export default translationStore
